import type { WorkspaceState } from "../schemas/workspace-state.schemas";

/** Base delay for exponential backoff in milliseconds */
const BASE_RECONNECT_DELAY_MS = 1000;

/** Maximum delay between reconnection attempts (cap for exponential backoff) */
const MAX_RECONNECT_DELAY_MS = 30_000;

/** Time to wait before disconnecting when tab is hidden (milliseconds) */
const VISIBILITY_DISCONNECT_DELAY_MS = 30_000;

export interface RealtimeConnectionOptions {
  url: string;
  projectId: string;
  token: string;
  /** Called when workspace state updates are received (null = expired/cleared) */
  onWorkspaceState?: (state: WorkspaceState | null) => void;
  /** Called when connection is established */
  onConnect?: () => void;
  /** Called when connection is closed (not due to error) */
  onClose?: () => void;
  /** Called on connection error */
  onError?: (error: Error) => void;
  /** Called on any message (for debugging/logging) */
  onMessage?: (data: unknown) => void;
  /** Called when reconnecting */
  onReconnecting?: (attempt: number) => void;
}

export interface RealtimeConnection {
  /** The underlying WebSocket instance */
  ws: WebSocket;
  /** Send a message to the server */
  send: (data: unknown) => void;
  /** Close the connection (prevents reconnection) */
  close: () => void;
  /** Connection state */
  isConnected: () => boolean;
}

/**
 * Create a managed WebSocket connection for realtime updates.
 * Handles connection lifecycle, event routing, automatic reconnection,
 * and visibility-based connection management.
 */
export async function createRealtimeConnection(
  options: RealtimeConnectionOptions,
): Promise<RealtimeConnection> {
  const { url, projectId, token, onWorkspaceState, onConnect, onClose, onError, onMessage, onReconnecting } = options;

  let currentWs: WebSocket | null = null;
  let intentionallyClosed = false;
  let disconnectedDueToVisibility = false;
  let reconnectAttempts = 0;
  let visibilityTimeout: ReturnType<typeof setTimeout> | null = null;

  const buildWsUrl = () => {
    const wsUrl = new URL(url);
    wsUrl.searchParams.set("projectId", projectId);
    wsUrl.searchParams.set("token", token);
    return wsUrl.toString();
  };

  const connect = (): Promise<WebSocket> => {
    return new Promise((resolve) => {
      const ws = new WebSocket(buildWsUrl());

      ws.onopen = () => {
        reconnectAttempts = 0; // Reset on successful connection
        disconnectedDueToVisibility = false;
        currentWs = ws;
        onConnect?.();
        resolve(ws);
      };

      ws.onerror = () => {
        // Error is handled in onclose
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Call generic message handler first
          onMessage?.(data);

          // Route specific message types
          if (data.type === "workspace_state" && "state" in data) {
            onWorkspaceState?.(data.state as WorkspaceState | null);
          }

          // Respond to server pings
          if (data.type === "ping") {
            ws.send(JSON.stringify({ type: "pong" }));
          }
        } catch {
          // Ignore parse errors for non-JSON messages
        }
      };

      ws.onclose = async (event) => {
        currentWs = null;

        // If intentionally closed, don't reconnect
        if (intentionallyClosed) {
          onClose?.();
          return;
        }

        // If closed due to visibility, don't auto-reconnect (will reconnect on visibility change)
        if (disconnectedDueToVisibility) {
          console.log("[realtime] Disconnected due to tab being hidden");
          return;
        }

        // If closed with error (code !== 1000), attempt reconnect forever
        if (event.code !== 1000) {
          reconnectAttempts++;

          // Log error on disconnect
          console.error(`[realtime] WebSocket disconnected unexpectedly (code: ${event.code}, reason: ${event.reason || "none"})`);
          onError?.(new Error(`WebSocket disconnected (code: ${event.code})`));

          // Calculate delay with exponential backoff, capped at MAX_RECONNECT_DELAY_MS
          const delay = Math.min(
            BASE_RECONNECT_DELAY_MS * Math.pow(2, reconnectAttempts - 1),
            MAX_RECONNECT_DELAY_MS
          );
          console.log(`[realtime] Reconnecting in ${delay}ms (attempt ${reconnectAttempts})`);
          onReconnecting?.(reconnectAttempts);

          await new Promise((r) => setTimeout(r, delay));

          // Check if we were intentionally closed during the wait
          if (intentionallyClosed) {
            return;
          }

          try {
            await connect();
          } catch (err) {
            // Reconnect failed, will be handled by next onclose
          }
        } else {
          // Clean close
          onClose?.();
        }
      };
    });
  };

  // Visibility change handler
  const handleVisibilityChange = async () => {
    console.log(`[realtime] Visibility changed: ${document.hidden ? "hidden" : "visible"}`);
    
    if (document.hidden) {
      // Tab is now hidden - start disconnect timer
      console.log(`[realtime] Tab hidden, will disconnect in ${VISIBILITY_DISCONNECT_DELAY_MS / 1000}s if still hidden`);
      visibilityTimeout = setTimeout(() => {
        if (currentWs && currentWs.readyState === WebSocket.OPEN) {
          console.log(`[realtime] Tab hidden for ${VISIBILITY_DISCONNECT_DELAY_MS / 1000}s, disconnecting...`);
          disconnectedDueToVisibility = true;
          currentWs.close();
        }
      }, VISIBILITY_DISCONNECT_DELAY_MS);
    } else {
      // Tab is now visible
      console.log(`[realtime] Tab visible again (disconnectedDueToVisibility=${disconnectedDueToVisibility}, currentWs=${currentWs ? "exists" : "null"})`);
      
      // Clear pending disconnect timer
      if (visibilityTimeout) {
        console.log("[realtime] Cancelled pending disconnect timer");
        clearTimeout(visibilityTimeout);
        visibilityTimeout = null;
      }

      // If disconnected due to visibility, reconnect
      if (disconnectedDueToVisibility && !currentWs) {
        console.log("[realtime] Was disconnected due to visibility, reconnecting...");
        disconnectedDueToVisibility = false;
        reconnectAttempts = 0;
        try {
          await connect();
          console.log("[realtime] Reconnected after visibility change");
        } catch (err) {
          console.error("[realtime] Failed to reconnect after visibility change:", err);
        }
      } else {
        console.log(`[realtime] No reconnection needed (disconnectedDueToVisibility=${disconnectedDueToVisibility}, hasWs=${!!currentWs})`);
      }
    }
  };

  // Add visibility change listener
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", handleVisibilityChange);
  }

  // Initial connection
  await connect();

  return {
    get ws() {
      return currentWs ?? null as any;
    },
    send: (data) => {
      if (currentWs && currentWs.readyState === WebSocket.OPEN) {
        currentWs.send(JSON.stringify(data));
      }
    },
    close: () => {
      intentionallyClosed = true;
      
      // Clean up visibility listener
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
      
      // Clear any pending visibility timeout
      if (visibilityTimeout) {
        clearTimeout(visibilityTimeout);
        visibilityTimeout = null;
      }
      
      currentWs?.close();
    },
    isConnected: () => currentWs?.readyState === WebSocket.OPEN,
  };
}
